import React, { useEffect, useMemo, useState } from 'react';

import { useSortedItems } from 'hooks/useSorting';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { DEBCreateExpertPopup } from 'components/DebFormComponents/Popup/DEBCreateExpertPopup';
import { DebExpertPopupEditContacts } from 'components/DebFormComponents/Popup/DebExpertPopupEditContacts';
import { ExpertPopupDelete } from 'components/DebFormComponents/Popup/ExpertPopupDelete';
import { ExtendExpertPopup } from 'components/DebFormComponents/Popup/ExtendExpertPopup';
import { ResetExpertBranchPopup } from 'components/DebFormComponents/Popup/ResetExpertBranchPopup';
import { BasicTable } from 'components/DebFormComponents/Table/BasicTable';
import { ExpertTableRows } from 'components/DebFormComponents/Table/ExpertTableRows';
import { ExpertTableInfoText } from 'components/ExpertTableInfoText/ExpertTableInfoText';
import { FormSection } from 'components/FormHelper/FormSection';
import { PlusIcon } from 'components/Icons/PlusIcon';
import { SubAccountsPopup } from 'components/SubAccounts/SubAccountsPopup';

import {
  BRANCH_TYPE,
  EIV_STATUS,
  EIV_STATUS_SALES_FORCE,
  EIV_STATUS_TO_SHOW,
  TENANT_ID,
} from 'global/constants';

import { getTenantId } from 'utils/tenantUtil';

import { CustomTableHeader } from './CustomTableHeader';
import { calcHasEnergy, calcHasGas } from './useInstallerBranchStatus';

import './Table.sass';

const headers = (hasEnergy, hasGas, updateSorting, sortingObject) => {
  const base = [
    <CustomTableHeader
      title="Name / Kontakt"
      updateKey="completeName"
      sortingObject={sortingObject}
      key="Name / Kontakt"
      updateSorting={updateSorting}
    />,
    ...(getTenantId() === TENANT_ID.BAG
      ? [
          <CustomTableHeader
            title="Geburtsdatum"
            updateKey="birthdate"
            sortingObject={sortingObject}
            key="GebDatum"
            updateSorting={updateSorting}
          />,
        ]
      : []),
  ];

  if (hasEnergy) {
    base.push(
      <CustomTableHeader
        title={
          <>
            Strom / Gültig bis <br /> (Status)
          </>
        }
        updateKey="statusEnergy"
        sortingObject={sortingObject}
        key="Strom"
        updateSorting={updateSorting}
      />
    );
  }
  if (hasGas) {
    base.push(
      <CustomTableHeader
        title={
          <>
            Gas / Gültig bis <br /> (Status)
          </>
        }
        updateKey="statusGas"
        sortingObject={sortingObject}
        key="Gas"
        updateSorting={updateSorting}
      />
    );
  }

  base.push(
    <CustomTableHeader
      align="left"
      key="Button"
      updateKey="Button"
      sortingObject={sortingObject}
      updateSorting={updateSorting}
      sorting={false}
    />
  );

  return base;
};

export const ExpertTable = ({
  data,
  oldData,
  formik,
  installer,
  installerStatusSummary,
  showMissingWarning,
  onExpertContactSaved,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [energyType, setEnergyType] = useState(null);
  const [gasType, setGasType] = useState(null);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openPopup, setPopupOpen] = useState(false);
  const [openExtendPopup, setOpenExtendPopup] = useState(false);
  const [openSubAccountPopup, setOpenSubAccountPopup] = useState(false);
  const [openEditContactsPopup, setOpenEditContactsPopup] = useState(false);
  const [openResetExpertBranchPopup, setOpenResetExpertBranchPopup] = useState(false);
  const [selectedResetBranch, setSelectedResetBranch] = useState(null);
  const [selectedExtendBranch, setSelectedExtendBranch] = useState(null);
  const [sortedRows, sortConfig, , updateSorting] = useSortedItems(data);

  useEffect(() => {
    if (installer) {
      setEnergyType(installerStatusSummary[BRANCH_TYPE.ENERGY].entryType);
      setGasType(installerStatusSummary[BRANCH_TYPE.GAS].entryType);
    }
  }, [installer]);

  const deleteExpert = (id, deletionData, branches) => {
    const newExperts = data
      .filter((expert) => expert.id !== id || !expert.isNewExpert)
      .map((expert) => {
        if (id !== expert.id) {
          return expert;
        }

        const oldExpert = oldData.find((expertFind) => expertFind.id === id);

        return {
          ...oldExpert,
          ...deletionData,
          ...(branches.includes(BRANCH_TYPE.GAS) && { statusGas: EIV_STATUS.DELETED }),
          ...(branches.includes(BRANCH_TYPE.ENERGY) && { statusEnergy: EIV_STATUS.DELETED }),
        };
      });
    formik.setFieldValue('experts', newExperts);
  };

  const undeleteExpert = (id) => {
    if (!data || !oldData) return;

    const oldExpert = oldData.find((expertFind) => expertFind.id === id);
    if (!oldExpert) return;

    const newExperts = data.map((expert) => {
      if (id !== expert.id) {
        return { ...expert };
      }

      return { ...oldExpert };
    });

    formik.setFieldValue('experts', newExperts);
  };

  const resetExpertExtensionBranches = (id, branches) => {
    if (!data || !oldData || !branches?.length) return;
    const oldExpert = oldData.find((expertFind) => expertFind.id === id);
    if (!oldExpert) return;
    const newExperts = data.map((expert) => {
      if (id !== expert.id) {
        return { ...expert };
      }

      return {
        ...expert,
        ...(branches.includes(BRANCH_TYPE.ENERGY) && {
          expertEnergyValidityDate: oldExpert.expertEnergyValidityDate,
          isExpertExtendedEnergy: oldExpert.isExpertExtendedEnergy,
        }),
        ...(branches.includes(BRANCH_TYPE.GAS) && {
          expertGasValidityDate: oldExpert.expertGasValidityDate,
          isExpertExtendedGas: oldExpert.isExpertExtendedGas,
        }),
      };
    });

    formik.setFieldValue('experts', newExperts);
  };

  const setSelectedId = (id) => {
    if (data.length) {
      let newExpertIndex = null;
      data.forEach((expertLoop, i) => {
        if (expertLoop) {
          if (expertLoop.id === id) {
            newExpertIndex = i;
          }
        }
      });
      if (newExpertIndex !== null) {
        setSelectedRow(newExpertIndex);
      }
    }
  };

  const gasExpert = useMemo(
    () =>
      data.filter((expertFilter) => {
        if (expertFilter && EIV_STATUS_TO_SHOW.includes(expertFilter.statusGas)) {
          return true;
        }
        return false;
      }),
    [data]
  );

  const energyExpert = useMemo(
    () =>
      data.filter((expertFilter) => {
        if (expertFilter && EIV_STATUS_TO_SHOW.includes(expertFilter.statusEnergy)) {
          return true;
        }
        return false;
      }),
    [data]
  );

  const hasEnergy = calcHasEnergy(installer);
  const hasGas = calcHasGas(installer);

  const showInfoText = useMemo(() => {
    if (data.length === 0) return true;

    const activeEnergyExperts = data.filter((expert) => {
      const isVisible = expert && EIV_STATUS_TO_SHOW.includes(expert.statusEnergy);
      const isDeleted = expert?.statusEnergy === EIV_STATUS.DELETED;
      return isVisible && !isDeleted;
    });

    const activeGasExperts = data.filter((expert) => {
      const isVisible = expert && EIV_STATUS_TO_SHOW.includes(expert.statusGas);
      const isDeleted = expert?.statusGas === EIV_STATUS.DELETED;
      return isVisible && !isDeleted;
    });

    const needsEnergyExpert = hasEnergy && activeEnergyExperts.length === 0;
    const needsGasExpert = hasGas && activeGasExperts.length === 0;
    const shouldShow = needsEnergyExpert || needsGasExpert;

    return shouldShow;
  }, [data, hasEnergy, hasGas]);

  const setCreatePopupOpen = (isOpen) => {
    setPopupOpen(isOpen);
    if (!isOpen) {
      setSelectedRow(null);
    }
  };

  const setExtendPopupOpen = (isOpen) => {
    setOpenExtendPopup(isOpen);
    if (!isOpen) {
      setSelectedExtendBranch(null);
      setSelectedRow(null);
    }
  };

  const setDeletePopupOpen = (isOpen) => {
    setOpenDeletePopup(isOpen);
    if (!isOpen) {
      setSelectedRow(null);
    }
  };

  const setEditContactsPopupOpen = (isOpen) => {
    setOpenEditContactsPopup(isOpen);
    if (!isOpen) {
      setSelectedRow(null);
    }
  };

  const setSubAccountPopupOpen = (isOpen) => {
    setOpenSubAccountPopup(isOpen);
    if (!isOpen) {
      setSelectedRow(null);
    }
  };

  const setResetExpertBranchPopupOpen = (isOpen) => {
    setOpenResetExpertBranchPopup(isOpen);
    if (!isOpen) {
      setSelectedResetBranch(null);
      setSelectedRow(null);
    }
  };

  const handleOpenExtendPopup = (rowId, branch) => {
    setSelectedId(rowId);
    setSelectedExtendBranch(branch);
    setExtendPopupOpen(true);
  };

  const handleOpenResetExpertBranchPopup = (rowId, branch) => {
    setSelectedId(rowId);
    setSelectedResetBranch(branch);
    setResetExpertBranchPopupOpen(true);
  };

  const handleOpenDeletePopup = (rowId) => {
    const selectedExpert = data.find((expert) => expert?.id === rowId);
    const isExpertNew =
      selectedExpert?.statusEnergy === EIV_STATUS.NEW ||
      selectedExpert?.statusGas === EIV_STATUS.NEW;

    if (isExpertNew) {
      deleteExpert(rowId, {}, []);
      return;
    }

    setSelectedId(rowId);
    setDeletePopupOpen(true);
  };
  const extendMainExpert = (id, branchType) => {
    const updatedExperts = formik.values.experts.map((expertLoop) => {
      if (expertLoop.id === id) {
        if (branchType === BRANCH_TYPE.ENERGY) {
          return {
            ...expertLoop,
            isExpertExtendedEnergy: true,
          };
        }
        if (branchType === BRANCH_TYPE.GAS) {
          return {
            ...expertLoop,
            isExpertExtendedGas: true,
          };
        }
        return expertLoop;
      }
      return expertLoop;
    });
    formik.setFieldValue('experts', updatedExperts);
  };
  return (
    <div className="expert-table__wrapper">
      <FormSection
        title="Fachkräfte"
        infoText={
          <ExpertTableInfoText showInfoText={showInfoText} hasEnergy={hasEnergy} hasGas={hasGas} />
        }>
        <FormSection isExtension>
          <span>
            Verlängerung maximal <span className="strong-text">180 Tage</span> vor dem Ablauf der
            Eintragung möglich.
          </span>
        </FormSection>
        <BasicTable
          className="expert-table"
          headers={headers(hasEnergy, hasGas, updateSorting, sortConfig)}
          rows={
            <ExpertTableRows
              rows={sortedRows}
              hasEnergy={hasEnergy}
              hasGas={hasGas}
              oldData={oldData}
              setSelectedId={setSelectedId}
              openExtendPopup={handleOpenExtendPopup}
              openResetExpertBranchPopup={handleOpenResetExpertBranchPopup}
              openDeletePopup={handleOpenDeletePopup}
              undeleteExpert={undeleteExpert}
              setOpenEditContactsPopup={setEditContactsPopupOpen}
              extendMainExpert={extendMainExpert}
              installerStatusSummary={installerStatusSummary}
              showMissingWarning={showMissingWarning}
            />
          }
        />
        <ActionButton
          inverted
          icon={<PlusIcon />}
          title="Weitere Fachkraft hinzufügen"
          onClick={() => {
            setSelectedRow(null);
            setCreatePopupOpen(true);
          }}
        />
      </FormSection>
      {openPopup && (
        <DEBCreateExpertPopup
          mainFormik={formik}
          selectedRow={selectedRow}
          popupOpen={openPopup}
          setPopupOpen={setCreatePopupOpen}
          installerStatusSummary={installerStatusSummary}
          setSelectedRow={setSelectedRow}
          energyEntryType={energyType}
          gasEntryType={gasType}
          onExpertContactSaved={onExpertContactSaved}
        />
      )}
      {openExtendPopup && (
        <ExtendExpertPopup
          mainFormik={formik}
          oldData={oldData}
          selectedRow={selectedRow}
          selectedBranch={selectedExtendBranch}
          popupOpen={openExtendPopup}
          setPopupOpen={setExtendPopupOpen}
          setSelectedRow={setSelectedRow}
          installer={installer}
          installerStatusSummary={installerStatusSummary}
        />
      )}
      {openDeletePopup && (
        <ExpertPopupDelete
          mainFormik={formik}
          selectedRow={selectedRow}
          popupOpen={openDeletePopup}
          setPopupOpen={setDeletePopupOpen}
          deleteExpert={deleteExpert}
          installerStatusSummary={installerStatusSummary}
        />
      )}
      {openEditContactsPopup && (
        <DebExpertPopupEditContacts
          mainFormik={formik}
          selectedRow={selectedRow}
          popupOpen={openEditContactsPopup}
          setPopupOpen={setEditContactsPopupOpen}
          setSelectedRow={setSelectedRow}
          onExpertContactSaved={onExpertContactSaved}
        />
      )}
      {openSubAccountPopup && (
        <SubAccountsPopup
          isOpen={openSubAccountPopup}
          selectedExpert={data[selectedRow]}
          onClose={() => setSubAccountPopupOpen(false)}
          onSetInProgress={(subFormik) => {
            const selectedExpert = data[selectedRow];
            if (selectedExpert) {
              selectedExpert.salesForceStatus = EIV_STATUS_SALES_FORCE.IN_PROGRESS;
              selectedExpert.netzportalEmail = subFormik.values.netzportalEmail;
              data[selectedRow] = selectedExpert;
              formik.setFieldValue('experts', data);
            }
          }}
        />
      )}
      {openResetExpertBranchPopup && (
        <ResetExpertBranchPopup
          mainFormik={formik}
          oldData={oldData}
          selectedRow={selectedRow}
          selectedBranch={selectedResetBranch}
          popupOpen={openResetExpertBranchPopup}
          setPopupOpen={setResetExpertBranchPopupOpen}
          onResetBranches={resetExpertExtensionBranches}
        />
      )}
    </div>
  );
};
ExpertTable.propTypes = {
  data: PropTypes.array,
  oldData: PropTypes.array,
  installer: PropTypes.object,
  formik: PropTypes.object,
  installerStatusSummary: PropTypes.object.isRequired,
  showMissingWarning: PropTypes.bool,
  onExpertContactSaved: PropTypes.func,
};
ExpertTable.defaultProps = {
  data: [],
  oldData: [],
  installer: {},
  formik: {},
  showMissingWarning: false,
  onExpertContactSaved: undefined,
};
